// src/pages/AnalyticsPage.js

/* eslint-disable react-hooks/exhaustive-deps */

import React, { useState, useEffect } from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

// Base API URL
const API_URL = process.env.REACT_APP_API_URL;

// Custom icons to match the theme
const Icons = {
    TrendingUp: (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
            <polyline points="17 6 23 6 23 12" />
        </svg>
    ),
    DollarSign: (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <line x1="12" y1="1" x2="12" y2="23" />
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
    ),
    ShoppingBag: (props) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <path d="M16 10a4 4 0 0 1-8 0" />
        </svg>
    )
};

const AnalyticsPage = () => {
    const [salesData, setSalesData] = useState([]);
    const [topSelling, setTopSelling] = useState([]);
    const [forecastData, setForecastData] = useState([]);
    const [summary, setSummary] = useState({ totalSales: 0, totalProductsSold: 0 });

    useEffect(() => {
        fetchSalesTrends();
        fetchTopSellingProducts();
        fetchSalesForecast();
        fetchSummaryData();
    }, []);

    const fetchSummaryData = async () => {
        try {
            const response = await fetch(`${API_URL}/analysis/summary`);
            if (!response.ok) throw new Error('Failed to fetch summary');
            const data = await response.json();

            // Convert string values to numbers
            const summaryWithNumbers = {
                totalSales: Number(data.totalSales),
                totalProductsSold: Number(data.totalProductsSold),
            };

            // Update the state with the numerical data
            setSummary(summaryWithNumbers);

        } catch (error) {
            console.error('Error fetching summary:', error);
        }
    };

    const fetchSalesTrends = async () => {
        try {
            const response = await fetch(`${API_URL}/analysis/sales-trends`);
            if (!response.ok) throw new Error('Failed to fetch sales trends');
            const data = await response.json();

            // This is the crucial step: convert the sales amount to a number
            const salesDataWithNumbers = data.sales.map(item => ({
                ...item,
                totalSalesAmount: Number(item.totalSalesAmount)
            }));

            setSalesData(salesDataWithNumbers);
        } catch (error) {
            console.error('Error fetching sales trends:', error);
        }
    };

    const fetchTopSellingProducts = async () => {
        try {
            const response = await fetch(`${API_URL}/analysis/top-selling-products`);
            if (!response.ok) throw new Error('Failed to fetch top selling products');
            const data = await response.json();
            setTopSelling(data.products);
        } catch (error) {
            console.error('Error fetching top selling products:', error);
        }
    };

    const fetchSalesForecast = async () => {
        try {
            const response = await fetch(`${API_URL}/analysis/forecast`);
            if (!response.ok) throw new Error('Failed to fetch forecast');
            const data = await response.json();
            setForecastData(data.forecast);
        } catch (error) {
            console.error('Error fetching forecast:', error);
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 font-sans p-8 text-gray-900">
            <div className="max-w-7xl mx-auto">
                <header className="mb-8">
                    <h1 className="text-4xl font-extrabold text-gray-900">
                        Analyse et Prévisions 📊
                    </h1>
                    <p className="mt-2 text-lg text-gray-500">
                        Obtenez un aperçu des performances de votre entreprise et anticipez les tendances futures.
                    </p>
                </header>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-white p-6 rounded-xl shadow-lg flex items-center space-x-4">
                        <Icons.DollarSign className="w-12 h-12 text-indigo-500" />
                        <div>
                            <h2 className="text-lg font-semibold text-gray-700">Chiffre d'Affaires Total</h2>
                            <p className="text-3xl font-bold text-gray-900 mt-1">
                                {summary.totalSales.toFixed(2)} CDF
                            </p>
                        </div>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-lg flex items-center space-x-4">
                        <Icons.ShoppingBag className="w-12 h-12 text-green-500" />
                        <div>
                            <h2 className="text-lg font-semibold text-gray-700">Total Produits Vendus</h2>
                            <p className="text-3xl font-bold text-gray-900 mt-1">
                                {summary.totalProductsSold}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Main Charts Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    {/* Sales Trend Chart */}
                    <div className="bg-white p-6 rounded-xl shadow-lg">
                        <h2 className="text-xl font-semibold text-gray-800 mb-4">Tendances des Ventes (Derniers 30 Jours)</h2>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={salesData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="logDate" /> {/* This is also a crucial key */}
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                {/* Make sure the dataKey here matches your backend data */}
                                <Line type="monotone" dataKey="totalSalesAmount" stroke="#8884d8" name="Chiffre d'affaires" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Sales Forecast Chart */}
                    <div className="bg-white p-6 rounded-xl shadow-lg">
                        <h2 className="text-xl font-semibold text-gray-800 mb-4">Prévisions des Ventes (Prochains 7 Jours)</h2>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={forecastData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Line type="monotone" dataKey="sales" stroke="#82ca9d" name="Ventes Prévues" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Top Selling Products */}
                <div className="bg-white p-6 rounded-xl shadow-lg">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">Top 5 des Produits les Plus Vendus</h2>
                    <div className="overflow-x-auto">
                        <table className="min-w-full table-auto">
                            <thead>
                                <tr className="bg-gray-50 text-gray-600 uppercase text-sm font-semibold">
                                    <th className="py-3 px-6 text-left rounded-tl-xl">Nom du Produit</th>
                                    <th className="py-3 px-6 text-right">Quantité Vendue</th>
                                </tr>
                            </thead>
                            <tbody className="text-gray-600 text-sm font-light divide-y divide-gray-200">
                                {topSelling.length > 0 ? (
                                    topSelling.map((product, index) => (
                                        <tr key={index} className="hover:bg-gray-50 transition-colors duration-200">
                                            <td className="py-3 px-6 text-left">{product.productName}</td>
                                            <td className="py-3 px-6 text-right font-medium">{product.totalSold}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="2" className="py-10 text-center text-gray-500">
                                            Aucune donnée de vente disponible.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AnalyticsPage;